"use client";

import { useMemo, useState } from "react";

export interface Testimonial {
  id: string;
  author_name: string;
  quote: string;
}

interface TestimonialCarouselProps {
  testimonials: Testimonial[];
  label: string;
  actionLabel?: string;
  reviewsLabel?: string;
  className?: string;
}

export function TestimonialCarousel({
  testimonials,
  label,
  actionLabel,
  reviewsLabel,
  className,
}: TestimonialCarouselProps) {
  const [index, setIndex] = useState(0);
  const safeIndex = useMemo(() => {
    if (testimonials.length === 0) return 0;
    return index % testimonials.length;
  }, [index, testimonials.length]);
  const current = useMemo(() => testimonials[safeIndex], [safeIndex, testimonials]);

  function goPrev() {
    setIndex((prev) => (prev === 0 ? testimonials.length - 1 : prev - 1));
  }

  function goNext() {
    setIndex((prev) => (prev === testimonials.length - 1 ? 0 : prev + 1));
  }

  if (testimonials.length === 0) return null;

  return (
    <section className={className}>
      <div className="flex items-center justify-between gap-4">
        <h2 className="text-xl font-semibold" style={{ color: "var(--color-text)" }}>
          {label}
        </h2>
        {actionLabel && (
          <button className="text-sm font-medium" style={{ color: "var(--color-muted)" }}>
            {actionLabel}
          </button>
        )}
      </div>

      <div className="mt-4 rounded-2xl border p-5 sm:p-6" style={{ borderColor: "var(--color-border)", background: "var(--color-bg)" }}>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1" aria-hidden>
            {Array.from({ length: 5 }).map((_, i) => (
              <svg key={i} width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style={{ color: "#eab308" }}>
                <path d="M12 2.5l2.9 5.88 6.49.95-4.7 4.58 1.11 6.47L12 17.33l-5.8 3.05 1.11-6.47-4.7-4.58 6.49-.95L12 2.5z" />
              </svg>
            ))}
          </div>
          {reviewsLabel && (
            <p className="text-sm font-medium" style={{ color: "var(--color-muted)" }}>
              {reviewsLabel}
            </p>
          )}
        </div>

        <div className="mt-4 text-sm leading-7 prose prose-sm max-w-none" style={{ color: "var(--color-text)" }} dangerouslySetInnerHTML={{ __html: current.quote }} />

        <p className="mt-3 text-sm font-semibold" style={{ color: "var(--color-muted)" }}>
          — {current.author_name}
        </p>

        {testimonials.length > 1 && (
          <div className="mt-5 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={goPrev}
                className="inline-flex h-8 w-8 items-center justify-center rounded-full border"
                style={{ borderColor: "var(--color-border)", color: "var(--color-muted)" }}
                aria-label="Previous review"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m15 18-6-6 6-6" />
                </svg>
              </button>
              <button
                type="button"
                onClick={goNext}
                className="inline-flex h-8 w-8 items-center justify-center rounded-full border"
                style={{ borderColor: "var(--color-border)", color: "var(--color-muted)" }}
                aria-label="Next review"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m9 18 6-6-6-6" />
                </svg>
              </button>
            </div>

            <div className="flex items-center gap-1.5" aria-label="Review position">
              {testimonials.map((item, i) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setIndex(i)}
                  className="h-1.5 rounded-full transition-all"
                  style={{
                    width: i === safeIndex ? "20px" : "6px",
                    background: i === safeIndex ? "var(--color-text)" : "var(--color-border)",
                  }}
                  aria-label={`Go to review ${i + 1}`}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
